import React, { useEffect, useRef } from 'react';

import type { BlobStyleProps, WaveformColors } from './types';

const DEFAULT_WAVEFORM_COLORS: WaveformColors = {
  core: '#FF8CC8',
  ring1: '#FF66A3',
  ring2: '#FF4D80',
  ring3: '#FF334D',
};

/** Parse `#RRGGBB` into the `r,g,b` string used inside rgba() literals. */
function hexToRgbString(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '255,255,255';
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 0xff},${(n >> 8) & 0xff},${n & 0xff}`;
}

/**
 * Waveform sphere — canvas, three concentric waveform rings around a glowing
 * core. Ported from the wiki mock. The waveform amplitude scales with `level`,
 * so a quiet idle reads as gentle ripples and a loud listening state reads
 * as deep waves.
 */
export const WaveformBlob: React.FC<BlobStyleProps & { colors?: WaveformColors }> = ({ level, size, colors }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const levelRef = useRef(level);
  useEffect(() => {
    levelRef.current = level;
  }, [level]);

  const colorsKey = colors ? `${colors.core}|${colors.ring1}|${colors.ring2}|${colors.ring3}` : 'default';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const cc = colors ?? DEFAULT_WAVEFORM_COLORS;
    const coreRgb = hexToRgbString(cc.core);
    const ringStrokes = [cc.ring1, cc.ring2, cc.ring3].map((hex) => hexToRgbString(hex));

    let raf: number;
    let t = 0;
    const draw = () => {
      t += 0.04;
      ctx.clearRect(0, 0, size, size);
      const cx = size / 2;
      const cy = size / 2;
      const l = levelRef.current;
      const segments = 120;
      const baseR = size * 0.227;

      const coreR = size * 0.136 + l * 10;
      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
      coreGrad.addColorStop(0, `rgba(255,255,255,${0.95})`);
      coreGrad.addColorStop(0.6, `rgba(${coreRgb},${0.6 + l * 0.3})`);
      coreGrad.addColorStop(1, 'rgba(80,20,80,0)');
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
      ctx.fill();

      for (let ring = 0; ring < 3; ring++) {
        ctx.beginPath();
        for (let i = 0; i <= segments; i++) {
          const a = (i / segments) * Math.PI * 2;
          const wave =
            Math.sin(a * (2 + ring) + t * (1 + ring * 0.5)) * (4 + l * 10) +
            Math.sin(a * (3 + ring) - t * 0.8) * (1.5 + l * 4);
          const r = baseR + ring * 8 + wave;
          const x = cx + Math.cos(a) * r;
          const y = cy + Math.sin(a) * r;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(${ringStrokes[ring]},${0.7 - ring * 0.18})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, colorsKey]);

  return <canvas ref={canvasRef} style={{ width: size, height: size }} aria-hidden />;
};

export default WaveformBlob;
